import React, { useId, useCallback, useRef, useState, useEffect } from 'react';
import {
  Backdrop,
  Box,
  CloseIcon,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  CircularProgress,
  Link,
  Slide,
  Typography,
  useMediaQuery,
  useTheme
} from '@/ui';
import DialogScreen from '@/engine/renderer/DialogScreen';
import Footer from '@/components/on-boarding/Footer';
import type { OpenActionSpec } from '@/shared/warnings/types';
import { useDOMErrorSuppression } from '@/shared/hooks/useDOMErrorSuppression';

type ScreenSchema = Parameters<typeof DialogScreen>[0]['schema'];

// Transition component for mobile slide-up
const Transition = React.forwardRef<unknown, { children: React.ReactElement }>(function Transition(
  props,
  ref,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface OnboardingDialogProps {
  isOpen: boolean;
  currentStep: number;
  totalSteps: number;
  currentSchema: ScreenSchema;
  screenRenderKey: string;
  formData: Record<string, unknown>;
  isLoading: boolean;
  computedPrimaryText: string;
  shouldShowSecondary: boolean;
  secondaryButtonText: string;
  onClose: () => void;
  onHelpOpen: () => void;
  onSubmit: (data: unknown) => Promise<void>;
  onValuesChange: (vals: unknown) => void;
  onBack: () => void;
  onSkip: () => void;
  onWarningAction: (action?: OpenActionSpec, metadata?: { varId?: string }) => boolean;
}

const OnboardingDialog: React.FC<OnboardingDialogProps> = ({
  isOpen,
  currentStep,
  totalSteps,
  currentSchema,
  screenRenderKey,
  formData,
  isLoading,
  computedPrimaryText,
  shouldShowSecondary,
  secondaryButtonText,
  onClose,
  onHelpOpen,
  onSubmit,
  onValuesChange,
  onBack,
  onSkip,
  onWarningAction,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const onboardingProgress = ((currentStep + 1) / totalSteps) * 100;
  
  // Generate unique IDs for accessibility
  const dialogTitleId = useId();
  const dialogDescriptionId = useId();

  // Track if we're in the process of closing (for animation and error suppression)
  const [isClosing, setIsClosing] = useState(false);
  
  // Refs to prevent double-firing when using both onMouseDown and onClick
  const closeActionFiredRef = useRef(false);
  const helpActionFiredRef = useRef(false);
  const closingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Activate DOM error suppression when dialog is open or closing
  useDOMErrorSuppression(isOpen || isClosing);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closingTimeoutRef.current) {
        clearTimeout(closingTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Safe close handler that:
   * 1. Prevents double-firing
   * 2. Sets closing state to trigger animation
   * 3. Delays actual close to allow DOM to stabilize
   */
  const handleSafeClose = useCallback(() => {
    if (closeActionFiredRef.current || isClosing) {
      return;
    }
    closeActionFiredRef.current = true;
    setIsClosing(true);
    
    // Clear any existing timeout
    if (closingTimeoutRef.current) {
      clearTimeout(closingTimeoutRef.current);
    }
    
    // Delay the actual close to allow:
    // 1. Any external DOM-mutating tools to process pending operations
    // 2. Animation to complete
    // 3. DOM to stabilize
    closingTimeoutRef.current = setTimeout(() => {
      closeActionFiredRef.current = false;
      setIsClosing(false);
      onClose();
    }, 150); // 150ms delay for stability
  }, [onClose, isClosing]);

  /**
   * Safe help handler with same double-fire protection
   */
  const handleSafeHelpOpen = useCallback(() => {
    if (helpActionFiredRef.current) {
      return;
    }
    helpActionFiredRef.current = true;
    // Reset after a short delay to allow for future clicks
    setTimeout(() => { helpActionFiredRef.current = false; }, 300);
    onHelpOpen();
  }, [onHelpOpen]);

  /**
   * Handle keyboard events for accessibility - allows Enter/Space to trigger actions
   * even when external tools are intercepting click events.
   */
  const handleCloseKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSafeClose();
    }
  }, [handleSafeClose]);

  const handleHelpKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSafeHelpOpen();
    }
  }, [handleSafeHelpOpen]);

  // Don't render if not open and not in closing animation
  if (!isOpen && !isClosing) {
    return null;
  }

  return (
    <Dialog
      open={isOpen && !isClosing}
      onClose={(_event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleSafeClose();
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={false}
      TransitionComponent={Transition}
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        }
      }}
      PaperProps={{
        sx: {
          ...(isMobile && {
            margin: 0,
            width: '100%',
            maxWidth: '100%',
            maxHeight: '90vh',
            borderRadius: '16px 16px 0 0',
            position: 'fixed',
            bottom: 0,
          }),
          ...(!isMobile && {
            borderRadius: 2,
            maxHeight: '90vh',
            margin: 2,
          }),
        }
      }}
      sx={{
        zIndex: 1200,
      }}
      aria-labelledby={dialogTitleId}
      aria-describedby={dialogDescriptionId}
    >
      <DialogTitle 
        id={dialogTitleId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          A quick step before we continue
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            color="text.secondary"
            onClick={handleSafeHelpOpen}
            onMouseDown={handleSafeHelpOpen}
            onKeyDown={handleHelpKeyDown}
            underline="always"
            tabIndex={0}
            role="button"
            sx={{ 
              cursor: 'pointer',
              '&:hover': {
                color: 'primary.main'
              },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
                borderRadius: 0.5,
              }
            }}
          >
            Help?
          </Link>
          <IconButton
            onClick={handleSafeClose}
            onMouseDown={handleSafeClose}
            onKeyDown={handleCloseKeyDown}
            size="small"
            aria-label="Close dialog"
            tabIndex={0}
            role="button"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Progress Bar */}
      <Box sx={{ px: 3, py: 2 }} role="progressbar" aria-valuenow={Math.round(onboardingProgress)} aria-valuemin={0} aria-valuemax={100} aria-label={`Step ${currentStep + 1} of ${totalSteps}`}>
        <LinearProgress
          variant="determinate"
          value={onboardingProgress}
          aria-hidden="true"
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#155F54',
              borderRadius: 3,
            }
          }}
        />
      </Box>

      <DialogContent 
        id={dialogDescriptionId}
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden'
        }}
      >
        {/* Screen Component */}
        <Box sx={{ 
          flex: 1,
          px: 3,
          py: 1,
          overflow: 'auto'
        }}>
          {isLoading ? (
            <Box 
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                minHeight: '240px',
                py: 2
              }}
              role="status"
              aria-live="polite"
              aria-label="Loading content"
            >
              <CircularProgress size={28} sx={{ color: '#155F54', mb: 1.5 }} aria-hidden="true" />
              <Typography variant="body2" color="text.secondary">Loading your info…</Typography>
            </Box>
          ) : (
            <DialogScreen
              key={screenRenderKey}
              schema={currentSchema}
              hideHeader={true}
              hideActions={true}
              initialData={formData}
              onSubmit={onSubmit}
              onValuesChange={onValuesChange}
              formId={`income-onboarding-form-${currentStep}` || ''}
              onWarningAction={onWarningAction || (() => false)}
            />
          )}
        </Box>
      </DialogContent>

      {/* Footer with Back and Continue buttons */}
      <Box>
        <Footer
          handleBack={onBack}
          shouldApplyTopBorder={false}
          shouldApplyBackgroundColor={false}
          shouldBackDisabled={currentStep === 0}
          nextButtonText={computedPrimaryText}
          formId={`income-onboarding-form-${currentStep}`}
          nextButtonLoading={isLoading}
          showSecondaryButton={shouldShowSecondary}
          secondaryButtonText={secondaryButtonText}
          onSecondaryClick={onSkip}
        />
      </Box>
    </Dialog>
  );
};

export default OnboardingDialog;

